import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_exam-excellence-hub/artifacts/e4f9i88p_trident%20logo.jpeg";

const navLinks = [
  { name: "Home", path: "/" },
  { name: "About", path: "/about" },
  { name: "Courses", path: "/courses" },
  { name: "Faculty", path: "/faculty" },
  { name: "Results", path: "/results" },
  { name: "Contact", path: "/contact" },
];

export default function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setIsOpen(false);
  }, [location]);

  return (
    <nav
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-white shadow-lg" : "bg-white/98 backdrop-blur-sm"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-4" data-testid="navbar-logo">
            <img
              src={LOGO_URL}
              alt="Trident Academy Logo"
              className="h-10 w-10 sm:h-12 sm:w-12 object-contain rounded"
            />

            
            <div className="flex flex-col leading-tight">
            <div
              className="font-extrabold text-sm sm:text-base tracking-widest"
              style={{ color: "#0A2540", fontFamily: "Manrope, sans-serif" }}
            >
              TRIDENT ACADEMY
            </div>
          
            <div
              className="text-[9px] sm:text-xs tracking-widest font-medium"
              style={{ color: "#F2C94C" }}
            >
              WHERE DREAMS MEET REALITY
            </div>
          </div>
            
          </Link>

          {/* Desktop Nav */}
          <div className="hidden lg:flex items-center gap-7">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`nav-link-${link.name.toLowerCase()}`}
                className={`text-sm font-semibold transition-all duration-200 pb-1 ${
                  location.pathname === link.path
                    ? "border-b-2 border-yellow-400"
                    : "text-gray-600 hover:text-[#0A2540] border-b-2 border-transparent"
                }`}
                style={location.pathname === link.path ? { color: "#0A2540" } : {}}
              >
                {link.name}
              </Link>
            ))}
          </div>

          {/* CTA */}
          <div className="hidden lg:flex items-center gap-3">
            <a
              href="tel:9867913757"
              className="flex items-center gap-1.5 text-sm font-medium"
              style={{ color: "#0A2540" }}
            >
              <Phone size={14} />
              9867913757
            </a>
            <Link
              to="/contact"
              data-testid="navbar-enquire-btn"
              className="text-white px-5 py-2 rounded-lg text-sm font-semibold transition-all duration-200 hover:opacity-90 active:scale-95"
              style={{ backgroundColor: "#0A2540" }}
            >
              Enquire Now
            </Link>
          </div>

          {/* Mobile Hamburger */}
          <button
            className="lg:hidden p-2 rounded-lg"
            style={{ color: "#0A2540" }}
            onClick={() => setIsOpen(!isOpen)}
            data-testid="navbar-mobile-menu-btn"
            aria-label="Toggle menu"
          >
            {isOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isOpen && (
          <div
            className="lg:hidden bg-white border-t border-gray-100 pb-4 shadow-lg"
            data-testid="mobile-menu"
          >
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`block px-4 py-3 text-sm font-semibold border-l-4 transition-colors ${
                  location.pathname === link.path
                    ? "bg-blue-50 border-yellow-400"
                    : "border-transparent text-gray-600 hover:bg-gray-50"
                }`}
                style={location.pathname === link.path ? { color: "#0A2540" } : {}}
              >
                {link.name}
              </Link>
            ))}
            <div className="px-4 pt-3 flex flex-col gap-2">
              <a
                href="tel:9867913757"
                className="flex items-center justify-center gap-2 text-sm font-medium border rounded-lg py-2"
                style={{ color: "#0A2540", borderColor: "#0A2540" }}
              >
                <Phone size={14} />
                9867913757
              </a>
              <Link
                to="/contact"
                className="block text-center text-white px-4 py-2.5 rounded-lg text-sm font-semibold"
                style={{ backgroundColor: "#0A2540" }}
              >
                Enquire Now
              </Link>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}
